import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import permissoesService from '../services/permissoesService';
import Toast from '../components/Toast';
import { UI_THEME } from '../ui/theme';

const PERMISSION_GROUPS = [
  { label: 'Comissões', permissions: ['MANAGE_COMISSOES'] },
  { label: 'Emendas', permissions: ['VIEW_EMENDAS', 'VIEW_EMENDAS_VISAO_GERAL', 'CREATE_EMENDA', 'EDIT_EMENDA', 'EDIT_EMENDA_VINCULO_ORCAMENTARIO', 'MANAGE_DOTACAO_ORCAMENTARIA'] },
  { label: 'Execução Financeira', permissions: ['VIEW_EXECUCAO_FINANCEIRA', 'REGISTER_MOVIMENTACAO_FINANCEIRA', 'ANULAR_MOVIMENTACAO_FINANCEIRA'] },
  {
    label: 'Fixo por perfil (dono da etapa)',
    permissions: [
      'APPROVE_ADMISSIBILIDADE',
      'ETAPA_EMENDA_DEMANDA',
      'ETAPA_EMENDA_DOCUMENTAL',
      'ETAPA_PLANO_ORCAMENTO',
      'ETAPA_PLANO_CONVENIOS_ANALISE',
      'ETAPA_PLANO_GESTOR_PARCERIA',
      'ETAPA_PLANO_COMISSAO_SELECAO',
      'ETAPA_PLANO_PROCURADORIA_JURIDICA',
      'ETAPA_PLANO_GESTOR_JURIDICO_REVISAO',
      'ETAPA_PLANO_CONVENIOS_MINUTA',
      'ETAPA_PLANO_CONVENIOS_FORMALIZACAO',
    ],
  },
  { label: 'Instituições', permissions: ['VIEW_INSTITUTIONS', 'CREATE_INSTITUTION', 'EDIT_INSTITUTION', 'MANAGE_INSTITUTION_DOCS', 'MANAGE_INSTITUTION_DIRIGENTES', 'APPROVE_INSTITUTION', 'BLOCK_INSTITUTION', 'INICIAR_ANALISE_INSTITUICAO'] },
  { label: 'Leis orçamentárias', permissions: ['MANAGE_LEIS_ORCAMENTARIAS'] },
  { label: 'Jurídico', permissions: ['ASSIGN_PROCURADOR', 'ACCESS_FILA_JURIDICA'] },
  { label: 'Parlamentares', permissions: ['VIEW_COUNCILORS', 'CREATE_COUNCILOR', 'EDIT_COUNCILOR', 'DELETE_COUNCILOR', 'MANAGE_PARLAMENTAR_LIMITES', 'VIEW_PARLAMENTAR_LIMITES', 'EDIT_PERFIL_PUBLICO_PARLAMENTAR'] },
  { label: 'Perfil', permissions: ['VIEW_PROFILE', 'EDIT_PROFILE'] },
  { label: 'Planos de Trabalho', permissions: ['VIEW_GESTAO_PLANOS', 'CREATE_PLANO_TRABALHO', 'WORKFLOW_DECISION_PLANO', 'UPLOAD_RETRO_ANEXO'] },
  { label: 'Pré-projetos', permissions: ['VIEW_PRE_PROJETOS', 'MANAGE_PRE_PROJETOS', 'SELECT_PRE_PROJETO'] },
  { label: 'Prestação de Contas', permissions: ['VIEW_PRESTACAO_CONTAS', 'PUBLICAR_EVIDENCIA_PRESTACAO', 'REGISTRAR_REPASSE_PRORROGACAO', 'GERENCIAR_COMPROVANTE_PRESTACAO'] },
  { label: 'Relatórios', permissions: ['VIEW_EMENDA_REPORTS', 'VIEW_INSTITUTION_REPORTS', 'VIEW_PENDENCIAS_REPORTS', 'VIEW_PRE_PROJETO_REPORTS', 'EXPORT_DATA'] },
  { label: 'Suporte e solicitações', permissions: ['CREATE_SOLICITACAO_ALTERACAO', 'TRATAR_SOLICITACAO_ALTERACAO'] },
  { label: 'Transparência (TAEP)', permissions: ['MANAGE_TAEP'] },
  { label: 'Tipos de Documentos', permissions: ['MANAGE_TIPOS_DOCUMENTO'] },
  { label: 'Usuários', permissions: ['VIEW_USERS', 'CREATE_USER', 'EDIT_USER', 'DELETE_USER', 'APPROVE_USER'] },
];

// Segregação de função e dono de etapa do workflow: o backend decide por role
// hardcoded (EmendasResource.podeExecutarAcao / PlanoTrabalhoService.validarPermissaoEtapa
// / PrestacaoContaService.usuarioPodeRegistrarRepasse-usuarioPodeAdicionarComprovante),
// não por esta permissão — a linha existe no banco só pra esta tela mostrar quem é o
// dono. Por isso o toggle fica travado: mudar aqui não mudaria o comportamento real,
// e deixar editável passaria a falsa impressão de que muda.
const FIXED_PERMISSIONS: Record<string, string[]> = {
  APPROVE_ADMISSIBILIDADE: ['ORCAMENTO'],
  REGISTRAR_REPASSE_PRORROGACAO: ['CONVENIOS'],
  GERENCIAR_COMPROVANTE_PRESTACAO: ['OPERADOR'],
  ETAPA_EMENDA_DEMANDA: ['SECRETARIA'],
  ETAPA_EMENDA_DOCUMENTAL: ['CONVENIOS'],
  ETAPA_PLANO_ORCAMENTO: ['ORCAMENTO'],
  ETAPA_PLANO_CONVENIOS_ANALISE: ['CONVENIOS'],
  ETAPA_PLANO_GESTOR_PARCERIA: ['GESTOR_PARCERIA'],
  ETAPA_PLANO_COMISSAO_SELECAO: ['CONVENIOS'],
  ETAPA_PLANO_PROCURADORIA_JURIDICA: ['JURIDICO'],
  ETAPA_PLANO_GESTOR_JURIDICO_REVISAO: ['GESTOR_JURIDICO'],
  ETAPA_PLANO_CONVENIOS_MINUTA: ['CONVENIOS'],
  ETAPA_PLANO_CONVENIOS_FORMALIZACAO: ['CONVENIOS'],
};

// Permissões que um perfil não consegue exercer porque o menu esconde o grupo inteiro
// por papel fixo (DashboardLayout: "Gestão"/"Cadastros" não aparecem para CAMARA,
// VEREADOR e OPERADOR). Conceder não teria efeito visível; o backend também recusa
// (RolePermissionService.PERMISSOES_BLOQUEADAS_POR_PERFIL) — manter em sincronia.
const MOTIVO_BLOQUEIO = 'Esse perfil não possui características para exercer esta função.';
const PERFIS_SEM_MENU_DE_GESTAO = ['CAMARA', 'VEREADOR', 'OPERADOR'];
const BLOCKED_PERMISSIONS: Record<string, string[]> = {
  MANAGE_PARLAMENTAR_LIMITES: PERFIS_SEM_MENU_DE_GESTAO,
  MANAGE_COMISSOES: PERFIS_SEM_MENU_DE_GESTAO,
  MANAGE_TAEP: [...PERFIS_SEM_MENU_DE_GESTAO, 'ORCAMENTO'],
  MANAGE_TIPOS_DOCUMENTO: PERFIS_SEM_MENU_DE_GESTAO,
  MANAGE_LEIS_ORCAMENTARIAS: PERFIS_SEM_MENU_DE_GESTAO,
  ACCESS_FILA_JURIDICA: PERFIS_SEM_MENU_DE_GESTAO,
};

const PERM_LABELS: Record<string, string> = {
  VIEW_EMENDAS: 'Ver Emendas',
  VIEW_EMENDAS_VISAO_GERAL: 'Ver Visão Geral de Emendas',
  CREATE_EMENDA: 'Criar Emenda',
  EDIT_EMENDA: 'Editar Emenda',
  EDIT_EMENDA_VINCULO_ORCAMENTARIO: 'Informar Vínculo Orçamentário (LOA) da Emenda',
  PUBLICAR_EVIDENCIA_PRESTACAO: 'Publicar Anexos da Prestação no Portal',
  MANAGE_TIPOS_DOCUMENTO: 'Configurar Tipos de Documentos',
  MANAGE_TAEP: 'Validar TAEP (autoavaliação de transparência)',
  CREATE_SOLICITACAO_ALTERACAO: 'Abrir solicitação (menu "Fale com a equipe")',
  TRATAR_SOLICITACAO_ALTERACAO: 'Tratar solicitações (menu "Suporte e solicitações")',
  MANAGE_LEIS_ORCAMENTARIAS: 'Cadastrar Leis Orçamentárias (LOA)',
  APPROVE_ADMISSIBILIDADE: 'Aprovar Admissibilidade',
  MANAGE_DOTACAO_ORCAMENTARIA: 'Gerenciar Dotação Orçamentária',
  VIEW_EXECUCAO_FINANCEIRA: 'Ver Execução Financeira',
  REGISTER_MOVIMENTACAO_FINANCEIRA: 'Registrar Movimentação Financeira',
  ANULAR_MOVIMENTACAO_FINANCEIRA: 'Anular Movimentação Financeira',
  VIEW_GESTAO_PLANOS: 'Ver Planos de Trabalho',
  CREATE_PLANO_TRABALHO: 'Criar plano de trabalho',
  VIEW_PRESTACAO_CONTAS: 'Ver Prestação de Contas',
  WORKFLOW_DECISION_PLANO: 'Tomar Decisão no Workflow',
  UPLOAD_RETRO_ANEXO: 'Upload Retroativo de Anexo',
  VIEW_INSTITUTIONS: 'Ver Instituições',
  CREATE_INSTITUTION: 'Criar Instituição',
  EDIT_INSTITUTION: 'Editar Instituição',
  DELETE_INSTITUTION: 'Excluir Instituição',
  MANAGE_INSTITUTION_DOCS: 'Gerenciar Documentos',
  MANAGE_INSTITUTION_DIRIGENTES: 'Gerenciar Dirigentes',
  APPROVE_INSTITUTION: 'Aprovar Instituição',
  BLOCK_INSTITUTION: 'Bloquear Instituição',
  INICIAR_ANALISE_INSTITUICAO: 'Iniciar Análise da Instituição',
  VIEW_PRE_PROJETOS: 'Ver Pré-projetos',
  MANAGE_PRE_PROJETOS: 'Gerenciar Pré-projetos',
  SELECT_PRE_PROJETO: 'Selecionar Pré-projeto',
  VIEW_COUNCILORS: 'Ver Parlamentares',
  CREATE_COUNCILOR: 'Criar Parlamentar',
  EDIT_COUNCILOR: 'Editar Parlamentar',
  DELETE_COUNCILOR: 'Excluir Parlamentar',
  MANAGE_PARLAMENTAR_LIMITES: 'Gerenciar Limites Parlamentares',
  VIEW_PARLAMENTAR_LIMITES: 'Consultar Limites Parlamentares (somente leitura)',
  EDIT_PERFIL_PUBLICO_PARLAMENTAR: 'Editar a própria página pública (parlamentar)',
  VIEW_USERS: 'Ver Usuários',
  CREATE_USER: 'Criar Usuário',
  EDIT_USER: 'Editar Usuário',
  DELETE_USER: 'Excluir Usuário',
  APPROVE_USER: 'Aprovar Usuário',
  VIEW_EMENDA_REPORTS: 'Relatórios de Emendas',
  VIEW_INSTITUTION_REPORTS: 'Relatórios de Instituições',
  VIEW_PENDENCIAS_REPORTS: 'Relatórios de Pendências',
  VIEW_PRE_PROJETO_REPORTS: 'Relatórios de Pré-projetos',
  EXPORT_DATA: 'Exportar Dados',
  ASSIGN_PROCURADOR: 'Atribuir Procurador',
  ACCESS_FILA_JURIDICA: 'Acessar Fila Jurídica',
  MANAGE_COMISSOES: 'Gerenciar Comissões',
  VIEW_PROFILE: 'Ver Perfil',
  EDIT_PROFILE: 'Editar Perfil',
  REGISTRAR_REPASSE_PRORROGACAO: 'Registrar Repasse / Prorrogar Prazo',
  GERENCIAR_COMPROVANTE_PRESTACAO: 'Inserir/Excluir Comprovante',
  ETAPA_EMENDA_DEMANDA: 'Emenda — Etapa Demanda',
  ETAPA_EMENDA_DOCUMENTAL: 'Emenda — Etapa Documental',
  ETAPA_PLANO_ORCAMENTO: 'Plano — Etapa Orçamento',
  ETAPA_PLANO_CONVENIOS_ANALISE: 'Plano — Etapa Análise (Convênios)',
  ETAPA_PLANO_GESTOR_PARCERIA: 'Plano — Etapa Gestor da Parceria',
  ETAPA_PLANO_COMISSAO_SELECAO: 'Plano — Etapa Comissão de Seleção',
  ETAPA_PLANO_PROCURADORIA_JURIDICA: 'Plano — Etapa Procuradoria (PGM)',
  ETAPA_PLANO_GESTOR_JURIDICO_REVISAO: 'Plano — Etapa Revisão Gestor Jurídico',
  ETAPA_PLANO_CONVENIOS_MINUTA: 'Plano — Etapa Minuta (Convênios)',
  ETAPA_PLANO_CONVENIOS_FORMALIZACAO: 'Plano — Etapa Formalização (Convênios)',
};

const ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Admin',
  ORCAMENTO: 'Orçamento',
  SECRETARIA: 'Secretaria',
  GESTOR_PARCERIA: 'Gestor da Parceria (adicional à Secretaria)',
  CONVENIOS: 'Convênios',
  OPERADOR: 'Operador',
  GESTOR_JURIDICO: 'Gestor Jurídico',
  ANALISTA: 'Analista',
  JURIDICO: 'Jurídico',
  JURIDICO_AUXILIAR: 'Auxiliar PGM',
  CAMARA: 'Câmara',
  VEREADOR: 'Parlamentar',
};

const EDITABLE_ROLES = [
  'ORCAMENTO',
  'SECRETARIA',
  'GESTOR_PARCERIA',
  'CONVENIOS',
  'OPERADOR',
  'GESTOR_JURIDICO',
  'ANALISTA',
  'JURIDICO',
  'JURIDICO_AUXILIAR',
  'CAMARA',
  'VEREADOR',
];

const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

const ChevronIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z" clipRule="evenodd" />
  </svg>
);

const SearchIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
    <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z" clipRule="evenodd" />
  </svg>
);

const LockIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
    <path fillRule="evenodd" d="M10 1a4.5 4.5 0 0 0-4.5 4.5V9H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2h-.5V5.5A4.5 4.5 0 0 0 10 1Zm3 8V5.5a3 3 0 1 0-6 0V9h6Z" clipRule="evenodd" />
  </svg>
);

const PermissoesConfigPage: React.FC = () => {
  useAuth();

  const [permissions, setPermissions] = useState<Record<string, string[]>>({});
  const [originalPermissions, setOriginalPermissions] = useState<Record<string, string[]>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [search, setSearch] = useState('');
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const [selectedRole, setSelectedRole] = useState<string>(EDITABLE_ROLES[0]);

  const isSaas = UI_THEME === 'saas';
  const surfaceClass = 'border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';
  const heroClass = isSaas
    ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
    : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';
  const accentTextClass = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const accentBgClass = isSaas ? 'bg-teal-600 dark:bg-teal-500' : 'bg-blue-700 dark:bg-blue-600';
  const accentBgHoverClass = isSaas ? 'hover:bg-teal-700 dark:hover:bg-teal-400' : 'hover:bg-blue-800 dark:hover:bg-blue-500';
  const bodyTextClass = 'text-slate-600 dark:text-slate-300';
  const labelTextClass = 'text-slate-500 dark:text-slate-400';
  const strongTextClass = 'text-slate-900 dark:text-slate-100';
  const saveButtonClass = isSaas
    ? 'bg-teal-600 text-white hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-400'
    : 'bg-blue-700 text-white hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-500';
  const switchAccentClass = isSaas ? 'peer-checked:bg-teal-600' : 'peer-checked:bg-blue-700';

  const fetchPermissions = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    permissoesService
      .getAll()
      .then(res => {
        setPermissions(res.data);
        setOriginalPermissions(res.data);
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const togglePermission = useCallback((role: string, perm: string) => {
    setPermissions(prev => {
      const current = prev[role] ?? [];
      const next = current.includes(perm)
        ? current.filter(p => p !== perm)
        : [...current, perm];
      return { ...prev, [role]: next };
    });
  }, []);

  const isRoleDirty = useCallback(
    (role: string) => {
      const current = new Set(permissions[role] ?? []);
      const original = new Set(originalPermissions[role] ?? []);
      if (current.size !== original.size) return true;
      for (const perm of current) if (!original.has(perm)) return true;
      return false;
    },
    [permissions, originalPermissions],
  );

  const dirtyRoles = useMemo(() => EDITABLE_ROLES.filter(isRoleDirty), [isRoleDirty]);

  const saveRole = useCallback(
    async (role: string) => {
      setSaving(prev => ({ ...prev, [role]: true }));
      try {
        await permissoesService.updateRole(role, permissions[role] ?? []);
        setOriginalPermissions(prev => ({ ...prev, [role]: permissions[role] ?? [] }));
        setToast({ message: `Permissões do perfil "${ROLE_LABELS[role] ?? role}" salvas com sucesso.`, type: 'success' });
      } catch {
        setToast({ message: `Erro ao salvar permissões do perfil "${ROLE_LABELS[role] ?? role}".`, type: 'error' });
      } finally {
        setSaving(prev => ({ ...prev, [role]: false }));
      }
    },
    [permissions],
  );

  const saveAllDirty = useCallback(async () => {
    for (const role of dirtyRoles) {
      // eslint-disable-next-line no-await-in-loop
      await saveRole(role);
    }
  }, [dirtyRoles, saveRole]);

  const discardRole = useCallback((role: string) => {
    setPermissions(prev => ({ ...prev, [role]: originalPermissions[role] ?? [] }));
  }, [originalPermissions]);

  const discardAll = useCallback(() => {
    setPermissions(originalPermissions);
  }, [originalPermissions]);

  const toggleGroup = useCallback((label: string) => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }, []);

  const searchTerm = normalize(search.trim());

  const visibleGroups = useMemo(() => {
    if (!searchTerm) return PERMISSION_GROUPS;
    return PERMISSION_GROUPS
      .map(group => ({
        label: group.label,
        permissions: group.permissions.filter(perm =>
          normalize(PERM_LABELS[perm] ?? perm).includes(searchTerm) || normalize(perm).includes(searchTerm),
        ),
      }))
      .filter(group => group.permissions.length > 0);
  }, [searchTerm]);

  const isGroupOpen = (label: string) => (searchTerm ? true : openGroups.has(label));
  const allOpen = openGroups.size === PERMISSION_GROUPS.length;
  const expandAll = () => setOpenGroups(new Set(PERMISSION_GROUPS.map(g => g.label)));
  const collapseAll = () => setOpenGroups(new Set());

  const totalPermissoes = PERMISSION_GROUPS.reduce((sum, group) => sum + group.permissions.length, 0);
  const grantedForSelected = permissions[selectedRole]?.length ?? 0;

  return (
    <div className="w-full space-y-6 pb-24">
      <section className={`rounded-3xl border ${heroClass}`}>
        <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${accentTextClass}`}>
          {isSaas ? 'Controle de acesso' : 'Administrador'}
        </p>
        <h1 className={`mt-2 text-2xl font-bold tracking-tight sm:text-3xl ${strongTextClass}`}>Permissões por perfil</h1>
        <p className={`mt-2 text-sm leading-6 ${bodyTextClass}`}>
          Escolha um perfil e marque o que ele pode fazer. Admin sempre tem acesso completo. Alterações valem a partir do próximo login do usuário.
        </p>

        {!loading && !loadError && (
          <div className="mt-5">
            <p className={`mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] ${labelTextClass}`}>Perfil</p>
            <div className="flex flex-wrap gap-1.5">
              {EDITABLE_ROLES.map(role => {
                const active = role === selectedRole;
                const dirty = isRoleDirty(role);
                const count = permissions[role]?.length ?? 0;
                return (
                  <button
                    key={role}
                    type="button"
                    onClick={() => setSelectedRole(role)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-xs font-semibold transition-colors ${
                      active
                        ? `${accentBgClass} border-transparent text-white`
                        : `border-slate-300 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700`
                    }`}
                  >
                    {dirty && <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-white' : 'bg-amber-500'}`} title="Alterações não salvas" />}
                    {ROLE_LABELS[role] ?? role}
                    <span className={`text-[10.5px] tabular-nums ${active ? 'text-white/80' : 'text-slate-400 dark:text-slate-500'}`}>
                      {count}/{totalPermissoes}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className={`mt-3 flex items-center gap-1.5 text-xs ${labelTextClass}`}>
              <LockIcon className="h-3.5 w-3.5 shrink-0" />
              Admin não aparece na lista acima — tem as {totalPermissoes} permissões sempre, não é configurável.
            </p>
          </div>
        )}
      </section>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className={`h-8 w-8 animate-spin rounded-full border-4 border-t-transparent ${isSaas ? 'border-teal-600 dark:border-teal-400' : 'border-blue-700 dark:border-blue-400'}`} />
        </div>
      ) : loadError ? (
        <div className={`flex flex-col items-center gap-3 rounded-3xl border py-16 text-center ${surfaceClass}`}>
          <p className={`text-sm ${bodyTextClass}`}>Não foi possível carregar as permissões configuradas no backend.</p>
          <button
            type="button"
            onClick={fetchPermissions}
            className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${saveButtonClass}`}
          >
            Tentar novamente
          </button>
        </div>
      ) : (
        <>
          <section className={`flex flex-wrap items-center gap-3 rounded-2xl border p-3 ${surfaceClass}`}>
            <div className="relative min-w-[200px] flex-1">
              <SearchIcon className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${labelTextClass}`} />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Buscar permissão, ex: prestação de contas"
                className={`w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm ${strongTextClass} placeholder:text-slate-400 focus:outline-none focus:ring-2 dark:border-slate-600 dark:bg-slate-800 ${isSaas ? 'focus:ring-teal-500' : 'focus:ring-blue-500'}`}
              />
            </div>
            <span className={`whitespace-nowrap px-1 text-xs ${labelTextClass}`}>
              <strong className={strongTextClass}>{grantedForSelected}</strong> de {totalPermissoes} marcadas
            </span>
            <button
              type="button"
              onClick={allOpen ? collapseAll : expandAll}
              disabled={!!searchTerm}
              className={`rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold ${bodyTextClass} hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:hover:bg-slate-800`}
            >
              {allOpen ? 'Recolher tudo' : 'Expandir tudo'}
            </button>
          </section>

          <section className={`overflow-hidden rounded-3xl border ${surfaceClass}`}>
            {visibleGroups.length === 0 ? (
              <div className={`p-10 text-center text-sm ${bodyTextClass}`}>
                Nenhuma permissão encontrada para "{search}".
              </div>
            ) : (
              visibleGroups.map(group => {
                const open = isGroupOpen(group.label);
                const grantedInGroup = group.permissions.filter(p =>
                  FIXED_PERMISSIONS[p] ? FIXED_PERMISSIONS[p].includes(selectedRole) : permissions[selectedRole]?.includes(p),
                ).length;
                return (
                  <div key={group.label} className="border-t border-slate-200 first:border-t-0 dark:border-slate-700">
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.label)}
                      disabled={!!searchTerm}
                      className="flex w-full items-center gap-2 bg-slate-50 px-4 py-2.5 text-left disabled:cursor-default dark:bg-slate-800/70"
                    >
                      <ChevronIcon className={`h-3.5 w-3.5 shrink-0 transition-transform ${labelTextClass} ${open ? 'rotate-180' : ''}`} />
                      <span className={`text-xs font-semibold uppercase tracking-wide ${labelTextClass}`}>{group.label}</span>
                      <span className={`text-[11px] font-medium ${labelTextClass} opacity-70`}>({group.permissions.length})</span>
                      <span className={`ml-auto text-[11px] font-medium tabular-nums ${labelTextClass} opacity-85`}>
                        {grantedInGroup}/{group.permissions.length}
                      </span>
                    </button>
                    {open && group.permissions.map(perm => {
                      const fixedOwners = FIXED_PERMISSIONS[perm];
                      const isFixed = fixedOwners !== undefined;
                      const checked = isFixed
                        ? fixedOwners.includes(selectedRole)
                        : permissions[selectedRole]?.includes(perm) ?? false;
                      // Bloqueado para o perfil: só permite desmarcar um grant legado.
                      const isBlocked = !!BLOCKED_PERMISSIONS[perm]?.includes(selectedRole) && !checked;
                      const lockedToggle = isFixed || isBlocked;
                      return (
                        <div
                          key={perm}
                          className="flex items-center gap-3 border-t border-slate-100 px-4 py-2.5 hover:bg-slate-50/80 dark:border-slate-800 dark:hover:bg-slate-800/60"
                        >
                          <span className={`flex-1 text-sm ${bodyTextClass}`}>
                            {PERM_LABELS[perm] ?? perm}
                            {isFixed && (
                              <span
                                className={`ml-2 inline-flex items-center gap-1 rounded-full border border-slate-300 px-1.5 py-0.5 align-middle text-[10px] font-semibold uppercase tracking-wide ${labelTextClass} dark:border-slate-600`}
                                title={`Fixo por desenho — o backend decide por perfil (${fixedOwners.map(r => ROLE_LABELS[r] ?? r).join(', ')}), não é ajustável por aqui`}
                              >
                                <LockIcon className="h-2.5 w-2.5" />
                                fixo
                              </span>
                            )}
                            {isBlocked && (
                              <span
                                className={`ml-2 inline-flex items-center gap-1 rounded-full border border-slate-300 px-1.5 py-0.5 align-middle text-[10px] font-semibold uppercase tracking-wide ${labelTextClass} dark:border-slate-600`}
                                title={MOTIVO_BLOQUEIO}
                              >
                                <LockIcon className="h-2.5 w-2.5" />
                                indisponível p/ este perfil
                                <span
                                  aria-label={MOTIVO_BLOQUEIO}
                                  className="inline-flex h-3.5 w-3.5 cursor-help items-center justify-center rounded-full border border-current text-[9px] font-bold"
                                >
                                  ?
                                </span>
                              </span>
                            )}
                          </span>
                          <label
                            className={`relative inline-flex h-[22px] w-[38px] shrink-0 items-center ${lockedToggle ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={lockedToggle}
                              onChange={() => !lockedToggle && togglePermission(selectedRole, perm)}
                              aria-label={`${PERM_LABELS[perm] ?? perm} — ${ROLE_LABELS[selectedRole] ?? selectedRole}${isFixed ? ' (fixo, não editável)' : isBlocked ? ' (indisponível para este perfil)' : ''}`}
                              className="peer sr-only"
                            />
                            <span className={`absolute inset-0 rounded-full bg-slate-300 transition-colors dark:bg-slate-600 ${switchAccentClass}`} />
                            <span className="absolute left-0.5 h-[18px] w-[18px] rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
                          </label>
                        </div>
                      );
                    })}
                  </div>
                );
              })
            )}
          </section>
        </>
      )}

      {dirtyRoles.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-4px_16px_rgba(15,23,42,0.08)] backdrop-blur dark:border-slate-700 dark:bg-slate-900/95">
          <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center gap-3">
            <span className={`text-xs font-semibold uppercase tracking-wide ${labelTextClass}`}>
              {dirtyRoles.length} {dirtyRoles.length === 1 ? 'perfil alterado' : 'perfis alterados'}
            </span>
            <div className="flex flex-1 flex-wrap items-center gap-2">
              {dirtyRoles.map(role => (
                <div
                  key={role}
                  className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 py-1 pl-3 pr-1.5 text-xs font-medium text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                >
                  <button type="button" onClick={() => setSelectedRole(role)} className="hover:underline">
                    {ROLE_LABELS[role] ?? role}
                  </button>
                  <button
                    type="button"
                    onClick={() => saveRole(role)}
                    disabled={saving[role]}
                    className="rounded-full bg-amber-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                  >
                    {saving[role] ? '...' : 'Salvar'}
                  </button>
                  <button
                    type="button"
                    onClick={() => discardRole(role)}
                    disabled={saving[role]}
                    className="rounded-full px-1.5 py-0.5 text-[11px] font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-50 dark:text-amber-300 dark:hover:bg-amber-900/40"
                    title="Descartar alteração deste perfil"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={discardAll}
                className={`rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold ${bodyTextClass} hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-800`}
              >
                Descartar tudo
              </button>
              <button
                type="button"
                onClick={saveAllDirty}
                className={`rounded-lg px-4 py-2 text-xs font-semibold text-white ${accentBgClass} ${accentBgHoverClass}`}
              >
                Salvar tudo
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default PermissoesConfigPage;
